import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import Button from '@material-ui/core/Button';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { keyEmptyCheck } from 'aurora-utils/utils';
import { AccordionLayout } from 'aurora-components';

import styles from './styles';

/** ****************
Props Documentation:
--------------------
label : label of the filter, it should be a lokalise key
data : array of object where each object should contain label and value
selected: it contain selected value, it should be an array of values in case of multi
multi: flag to check if multiple selection is required or not
isAccordion: flag to check if accordion layout is required
onChange: A handler which will trigger every time when an item is clicked
customClasses: A object which contain custom classNames to override styles
***************** */

export const SHAPES = {
  SQUARE: 'SQUARE',
  OVAL: 'OVAL',
};

const TilesSelect = ({
  children,
  classes,
  name,
  label,
  value,
  options,
  multi,
  isAccordion,
  shape,
  onChange,
  disabled,
  customClasses,
  ...fieldProps
}) => {
  if (!isArray(options) || isEmpty(options)) return null;

  const selectedItems = useMemo(() => (multi && isArray(value) ? value : []), [value]);

  const onChangeHandler = data => {
    if (!multi) {
      onChange(name, value === data ? null : data);
      return;
    }
    if (selectedItems?.includes(data)) {
      onChange(
        name,
        selectedItems?.filter(selectedValue => selectedValue !== data),
      );
    } else {
      onChange(name, [...selectedItems, data]);
    }
  };

  const renderChildren = () =>
    !isEmpty(children) ? <div className={classes.childrenContainer}>{children}</div> : null;

  const renderItems = dataList => {
    return (
      <div className={classNames(classes.list, customClasses?.list)}>
        {dataList?.map(item => (
          <Button
            className={classNames(
              classes.listItem,
              customClasses?.listItem,
              {
                [classes.listItemSelected]: multi ? selectedItems?.includes(item?.value) : value === item?.value,
              },
              {
                [classes.listItemSquare]: shape === SHAPES.SQUARE,
              },
              {
                [classes.listItemDisabled]: disabled,
              },
            )}
            onClick={() => onChangeHandler(item?.value)}
            disabled={disabled}
          >
            {item?.label}
          </Button>
        ))}
      </div>
    );
  };

  if (isAccordion) {
    return (
      <AccordionLayout title={label} required={fieldProps?.required}>
        {renderItems(options)}
        {renderChildren()}
      </AccordionLayout>
    );
  }

  return (
    <div className={classNames(classes.container, customClasses?.container)}>
      <p className={classNames(classes.label, customClasses?.label)}>{keyEmptyCheck(label)}</p>
      {renderItems(options)}
      {renderChildren()}
    </div>
  );
};

TilesSelect.propTypes = {
  children: PropTypes.node.isRequired,
  classes: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.any,
  multi: PropTypes.bool,
  shape: PropTypes.string,
  disabled: PropTypes.bool,
  isAccordion: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  customClasses: PropTypes.array,
};

TilesSelect.defaultProps = {
  multi: false,
  disabled: false,
  value: null,
  isAccordion: false,
  customClasses: {},
  shape: SHAPES.OVAL,
};

export default withStyles(styles)(TilesSelect);
